import { useEffect, useRef } from 'react';
import { initializeWebSocket } from '../lib/websocket';
import { IChatConversation, IChatMessage } from '../interfaces/chat/chat.interface';
import { IConversationUpdatedPayload, IEchoInstance, IMessageSentPayload } from '../interfaces/chat/websocket.interface';

type ChannelInstance = {
  listen: (event: string, callback: (payload: unknown) => void) => void;
  stopListening: (event: string) => void;
};

export const useRealtimeChat = (
  conversations: IChatConversation[],
  setConversations: React.Dispatch<React.SetStateAction<IChatConversation[]>>,
  selectedConversationId: number | null,
  currentUserId: number | null,
  setMessages: React.Dispatch<React.SetStateAction<IChatMessage[]>>,
  enabled: boolean = true,
) => {
  const channelsRef = useRef<Map<number, ChannelInstance>>(new Map());
  const userChannelRef = useRef<ChannelInstance | null>(null);
  const echoRef = useRef<IEchoInstance | null>(null);
  const conversationIdsRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!enabled || !currentUserId) return;

    let cleanup: (() => void) | undefined;

    const setupWebSocket = async () => {
      try {
        // Usar echoRef se já existir, senão inicializar
        let echo = echoRef.current;
        if (!echo) {
          echo = await initializeWebSocket();
          if (!echo) {
            return;
          }
          echoRef.current = echo;
        }

        // Verificar novamente se echo não é null (para TypeScript)
        if (!echo) {
          return;
        }

        // Obter IDs das conversas atuais
        const currentConversationIds = new Set(conversations.map((c) => c.id));

        // Remover listeners de conversas que não existem mais
        channelsRef.current.forEach((channel, conversationId) => {
          if (!currentConversationIds.has(conversationId)) {
            try {
              channel.stopListening('.message.sent');
              channelsRef.current.delete(conversationId);
              conversationIdsRef.current.delete(conversationId);
            } catch (error) {
              // Ignorar erros
            }
          }
        });

        // Configurar user channel apenas uma vez
        if (!userChannelRef.current) {
          const userChannel = echo.private(`chat.user.${currentUserId}`);
          userChannelRef.current = userChannel;

          userChannel.listen('.conversation.updated', (payload: unknown) => {
            const data = payload as IConversationUpdatedPayload;
            setConversations((prev) =>
              prev.map((conv) => {
                if (conv.id === data.conversation_id) {
                  const updated: Partial<IChatConversation> = {};
                  if (data.type && (data.type === 'direct' || data.type === 'group')) {
                    updated.type = data.type;
                  }
                  return { ...conv, ...updated };
                }
                return conv;
              }),
            );
          });
        }

        // Escutar mensagens novas para cada conversa
        conversations.forEach((conversation) => {
          try {
            // Verificar se já existe um listener para esta conversa
            if (conversationIdsRef.current.has(conversation.id)) {
              return; // Já tem listener, não criar outro
            }

            // Verificação adicional para TypeScript dentro do forEach
            if (!echo) {
              return;
            }

            const channel = echo.private(`chat.conversation.${conversation.id}`);
            if (!channel) {
              return;
            }
            
            channelsRef.current.set(conversation.id, channel);
            conversationIdsRef.current.add(conversation.id);

            channel.listen('.message.sent', (payload: unknown) => {
              const data = payload as IMessageSentPayload;
              const newMessage: IChatMessage = {
                id: data.id,
                conversationId: data.conversation_id,
                senderId: data.sender_id,
                content: data.content,
                messageType: data.message_type as 'text' | 'image' | 'file',
                createdAt: data.created_at,
                timestamp: new Date(data.created_at).toLocaleTimeString('pt-BR', {
                  hour: '2-digit',
                  minute: '2-digit',
                }),
                isOwn: data.sender_id === currentUserId,
                sender: data.sender,
              };

              if (data.conversation_id === selectedConversationId) {
                setMessages((prev) => {
                  const exists = prev.some((msg) => msg.id === data.id);
                  if (exists) return prev;
                  
                  if (data.sender_id === currentUserId) {
                    const now = Date.now();
                    const optimisticIndex = prev.findIndex((msg) => {
                      if (msg.status !== 'sending' || !msg.isOwn || msg.content !== data.content) {
                        return false;
                      }
                      const msgTime = new Date(msg.createdAt).getTime();
                      return Math.abs(now - msgTime) < 5000; // 5 segundos
                    });
                    
                    if (optimisticIndex !== -1) {
                      const newPrev = [...prev];
                      newPrev[optimisticIndex] = { ...newMessage, status: 'sent' as const };
                      return newPrev;
                    }
                  }
                  
                  return [...prev, newMessage];
                });
              }

              setConversations((prev) => {
                const updated = prev.map((conv) => {
                  if (conv.id === data.conversation_id) {
                    return {
                      ...conv,
                      lastMessage: data.content,
                      timestamp: newMessage.timestamp,
                      unreadCount:
                        data.conversation_id === selectedConversationId
                          ? conv.unreadCount || 0
                          : (conv.unreadCount || 0) + 1,
                    };
                  }
                  return conv;
                });
                
                const conversationIndex = updated.findIndex((c) => c.id === data.conversation_id);
                if (conversationIndex > 0) {
                  const [movedConversation] = updated.splice(conversationIndex, 1);
                  return [movedConversation, ...updated];
                }
                
                return updated;
              });
            });
          } catch (error) {
            // Erro ao configurar listener para conversa
          }
        });

        cleanup = () => {
          try {
            channelsRef.current.forEach((channel) => {
              try {
                channel.stopListening('.message.sent');
              } catch (error) {
                // Ignorar erros
              }
            });
            channelsRef.current.clear();

            if (userChannelRef.current) {
              try {
                userChannelRef.current.stopListening('.conversation.updated');
                userChannelRef.current = null;
              } catch (error) {
                // Ignorar erros
              }
            }
          } catch (error) {
            // Erro no cleanup
          }
        };
      } catch (error) {
        // Erro ao configurar WebSocket
      }
    };

    setupWebSocket();

    return () => {
      if (cleanup) {
        cleanup();
      }
      channelsRef.current.forEach((channel) => {
        try {
          channel.stopListening('.message.sent');
        } catch (error) {
          // Ignorar erros
        }
      });
      channelsRef.current.clear();
      conversationIdsRef.current.clear();
      if (userChannelRef.current) {
        try {
          userChannelRef.current.stopListening('.conversation.updated');
        } catch (error) {
          // Ignorar erros
        }
        userChannelRef.current = null;
      }
      echoRef.current = null;
    };
  }, [enabled, currentUserId, selectedConversationId, conversations.map((c) => c.id).join(',')]);
};

